import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { getTenant, getTenantEvents, getTenantReadiness, updateTenant } from '../api/authApi';
import { getTenantStoragePaths } from '../api/providersApi';
import { colorTokens, typographyTokens } from '../app/designTokens';
import { DataTable } from '../components/DataTable';
import { DetailField, InfoCard } from '../components/InfoCard';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { PlaceholderPanel } from '../components/PlaceholderPanel';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { SupportTicketsTable } from '../components/SupportTicketsTable';
import { TenantReadinessPanel } from '../components/TenantReadinessPanel';
import { TenantEntitlementsTab } from '../components/TenantEntitlementsTab';
import { TenantAccessPanel } from '../components/TenantAccessPanel';
import { TenantSetupWizardTab } from '../components/TenantSetupWizardTab';
import { TenantConfigurationReadinessCard } from '../components/TenantConfigurationReadinessCard';
import { TenantLaunchReadinessPanel } from '../components/TenantLaunchReadinessPanel';
import { TenantProvisioningTab } from '../components/TenantProvisioningTab';
import { TenantSchemaTab } from '../components/TenantSchemaTab';
import { TenantBillingTab } from '../components/TenantBillingTab';
import { TenantStorageSmokeTestCard } from '../components/TenantStorageSmokeTestCard';
import { Button } from '../components/ui/Button';
import { Tab, Tabs } from '../components/ui/Tabs';
import { TextInput } from '../components/ui/TextInput';
import {
  formatProvisioningStatus,
  formatTenantStatus,
  formatUnknownCode,
} from '../utils/systemLabels';
import { runTenantDetailVisibleTextAudit } from '../utils/visibleTextAudit';

const { semantic } = colorTokens;

export function TenantDetailPage() {
  const { t, i18n } = useTranslation(['tenants', 'common', 'statuses', 'pricing', 'storage']);
  const { id } = useParams();
  const [tenant, setTenant] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsLoaded, setEventsLoaded] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [readinessLoaded, setReadinessLoaded] = useState(false);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [storagePaths, setStoragePaths] = useState(null);
  const [storagePathsLoaded, setStoragePathsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);
  const [prefixDraft, setPrefixDraft] = useState('');
  const [prefixSaving, setPrefixSaving] = useState(false);
  const [prefixError, setPrefixError] = useState(null);
  const [prefixMessage, setPrefixMessage] = useState(null);

  const tabs = useMemo(() => [
    t('tenants:tabs.overview'),
    t('tenants:tabs.access_status'),
    t('tenants:tabs.readiness'),
    t('tenants:tabs.go_no_go'),
    t('tenants:tabs.database'),
    t('tenants:tabs.storage'),
    t('tenants:tabs.branding'),
    t('tenants:tabs.features'),
    t('tenants:tabs.support'),
    t('tenants:tabs.events'),
    t('tenants:tabs.setup_wizard'),
    t('tenants:tabs.provisioning'),
    t('tenants:tabs.schema'),
    t('tenants:tabs.billing'),
    t('tenants:tabs.audit'),
  ], [t]);

  const formatDate = useCallback((value) => {
    if (!value) {
      return t('common:empty_value');
    }

    return new Date(value).toLocaleString(i18n.language);
  }, [i18n.language, t]);

  const translateStatus = (group, value) => {
    if (!value) {
      return t('common:empty_value');
    }

    const key = `statuses:${group}.${value}`;
    return i18n.exists(key) ? t(key) : value;
  };

  const translateAccess = (allowed) => (
    allowed ? t('statuses:access.allowed') : t('statuses:access.blocked')
  );

  useEffect(() => {
    setLoading(true);
    setError(null);

    getTenant(id)
      .then((data) => {
        setTenant(data);
        setPrefixDraft(data?.support_ticket_prefix || '');
      })
      .catch(() => setError(t('tenants:load_failed')))
      .finally(() => setLoading(false));
  }, [id, t]);

  const saveSupportTicketPrefix = async () => {
    const normalized = String(prefixDraft || '').trim().toUpperCase();
    if (normalized && !/^[A-Z0-9][A-Z0-9-]{1,15}$/.test(normalized)) {
      setPrefixError(t('tenants:support_ticket_prefix_invalid'));
      setPrefixMessage(null);
      return;
    }

    setPrefixSaving(true);
    setPrefixError(null);
    setPrefixMessage(null);
    try {
      const updated = await updateTenant(id, {
        support_ticket_prefix: normalized || null,
      });
      setTenant(updated);
      setPrefixDraft(updated?.support_ticket_prefix || '');
      setPrefixMessage(t('tenants:support_ticket_prefix_saved'));
    } catch {
      setPrefixError(t('tenants:support_ticket_prefix_save_failed'));
    } finally {
      setPrefixSaving(false);
    }
  };

  useEffect(() => {
    if (tab !== 9 || eventsLoaded || !id) {
      return;
    }

    getTenantEvents({ tenant_id: id })
      .then((response) => {
        setEvents(response.data || []);
        setEventsLoaded(true);
      })
      .catch(() => setEvents([]))
      .finally(() => setEventsLoaded(true));
  }, [tab, id, eventsLoaded]);

  useEffect(() => {
    if (tab !== 2 || readinessLoaded || !id) {
      return;
    }

    setReadinessLoading(true);

    getTenantReadiness(id)
      .then(setReadiness)
      .catch(() => setReadiness(null))
      .finally(() => {
        setReadinessLoaded(true);
        setReadinessLoading(false);
      });
  }, [tab, id, readinessLoaded]);

  useEffect(() => {
    if (tab !== 5 || storagePathsLoaded || !id) {
      return;
    }

    getTenantStoragePaths(id)
      .then((data) => {
        setStoragePaths(data);
        setStoragePathsLoaded(true);
      })
      .catch(() => {
        setStoragePaths(null);
        setStoragePathsLoaded(true);
      });
  }, [tab, id, storagePathsLoaded]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production' || loading || !tenant) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      runTenantDetailVisibleTextAudit(document.body);
    }, 700);
    return () => window.clearTimeout(timer);
  }, [loading, tenant, tab, readiness, events, storagePaths]);

  const emptyValue = t('common:empty_value');

  const eventColumns = useMemo(() => [
    {
      id: 'type',
      label: t('common:type'),
      render: (event) => {
        const typeKey = String(event.event_type || '').replace(/\./g, '_');
        const typeI18n = `tenants:events.types.${typeKey}`;
        return i18n.exists(typeI18n)
          ? t(typeI18n)
          : (formatUnknownCode(event.event_type) || emptyValue);
      },
    },
    {
      id: 'severity',
      label: t('common:severity'),
      render: (event) => formatUnknownCode(event.severity) || emptyValue,
    },
    {
      id: 'source',
      label: t('common:source'),
      render: (event) => {
        const sourceKey = String(event.source || '').replace(/\./g, '_');
        const sourceI18n = `tenants:events.sources.${sourceKey}`;
        return i18n.exists(sourceI18n)
          ? t(sourceI18n)
          : (formatUnknownCode(event.source) || emptyValue);
      },
    },
    {
      id: 'created',
      label: t('common:created'),
      render: (event) => formatDate(event.created_at),
    },
  ], [emptyValue, formatDate, i18n, t]);

  if (loading) {
    return <LoadingSkeleton type="Detail" />;
  }

  if (error || !tenant) {
    return (
      <SectionCard variant="Danger">
        <Alert severity="error" sx={{ bgcolor: 'transparent', p: 0 }}>
          {error || t('tenants:not_found')}
        </Alert>
      </SectionCard>
    );
  }

  const slug = tenant.slug || '';
  const storagePrefix = storagePaths?.s3_prefix || tenant.s3_prefix || `tenants/${slug}/`;
  const storageExamples = storagePaths?.paths
    ? Object.values(storagePaths.paths)
    : [
        `${storagePrefix}branding/`,
        `${storagePrefix}support/tickets/`,
        `${storagePrefix}exports/`,
      ];

  return (
    <Box>
      <PageHeader
        type="Detail"
        title={tenant.name}
        subtitle={tenant.slug}
        backTo="/tenants"
        badge={(
          <StatusBadge
            status={tenant.status}
            category="tenant"
            apiLabel={formatTenantStatus(tenant.status)}
            size="Md"
          />
        )}
      />

      <Tabs value={tab} onChange={(_, value) => setTab(value)}>
        {tabs.map((label) => (
          <Tab key={label} label={label} />
        ))}
      </Tabs>

      {tab === 0 && (
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard label={t('common:status')}>
                <StatusBadge
                  status={tenant.status}
                  category="tenant"
                  apiLabel={formatTenantStatus(tenant.status)}
                  size="Sm"
                />
              </InfoCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard label={t('common:slug')} value={tenant.slug || emptyValue} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard label={t('common:customer')} value={tenant.customer?.name || emptyValue} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                label={t('tenants:fields.primary_domain')}
                value={tenant.primary_domain?.host || emptyValue}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                label={t('common:environment')}
                value={formatProvisioningStatus(tenant.database?.status) || tenant.database?.status || emptyValue}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard label={t('common:created')} value={formatDate(tenant.created_at)} />
            </Grid>
          </Grid>

          <TenantConfigurationReadinessCard readiness={tenant.configuration_readiness} />

          <SectionCard
            variant="Default"
            title={t('tenants:fields.support_ticket_prefix')}
            subtitle={t('tenants:fields.support_ticket_prefix_help')}
            sx={{ mt: 2, mb: 0 }}
          >
            {prefixMessage && <Alert severity="success" sx={{ mb: 2 }}>{prefixMessage}</Alert>}
            {prefixError && <Alert severity="error" sx={{ mb: 2 }}>{prefixError}</Alert>}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <TextInput
                density="Compact"
                label={t('tenants:fields.support_ticket_prefix')}
                value={prefixDraft}
                onChange={(e) => setPrefixDraft(e.target.value.toUpperCase())}
                inputProps={{ maxLength: 16, 'data-testid': 'tenant-support-ticket-prefix' }}
                sx={{ minWidth: 220 }}
              />
              <Button
                variant="Primary"
                onClick={saveSupportTicketPrefix}
                disabled={prefixSaving}
                loading={prefixSaving}
                data-testid="tenant-support-ticket-prefix-save"
              >
                {t('common:save')}
              </Button>
            </Box>
          </SectionCard>

          {tenant.customer?.id && (
            <SectionCard variant="Default" title={t('tenants:customer_link')} sx={{ mt: 2, mb: 0 }}>
              <Typography
                component={RouterLink}
                to={`/customers/${tenant.customer.id}`}
                sx={{
                  fontFamily: typographyTokens.fontFamily,
                  fontWeight: typographyTokens.fontWeightSemibold,
                  fontSize: 14,
                  color: semantic.text.primary,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline', color: semantic.text.secondary },
                }}
              >
                {tenant.customer.name}
              </Typography>
            </SectionCard>
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <SectionCard variant="Default" sx={{ mb: 0 }}>
            <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
              {t('tenants:legacy_access_flag', {
                access: translateAccess(tenant.is_access_allowed),
                status: translateStatus('tenant', tenant.status),
              })}
            </Typography>
          </SectionCard>
          <TenantAccessPanel tenantId={tenant.id} />
        </Box>
      )}

      {tab === 2 && (
        <TenantReadinessPanel readiness={readiness} loading={readinessLoading && !readinessLoaded} />
      )}

      {tab === 3 && (
        <TenantLaunchReadinessPanel tenantId={tenant.id} />
      )}

      {tab === 4 && (
        <SectionCard variant="Default" title={t('tenants:tabs.database')}>
          {tenant.database ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2.5,
              }}
            >
              <DetailField label={t('tenants:fields.database_name')} value={tenant.database.db_name} />
              <DetailField
                label={t('common:host')}
                value={`${tenant.database.db_host}:${tenant.database.db_port}`}
              />
              <DetailField
                label={t('common:username')}
                value={tenant.database.db_username || emptyValue}
              />
              <DetailField label={t('common:status')}>
                <StatusBadge
                  status={tenant.database.status}
                  apiLabel={formatProvisioningStatus(tenant.database.status) || tenant.database.status}
                  size="Sm"
                />
              </DetailField>
            </Box>
          ) : (
            <PlaceholderPanel message={t('tenants:no_database')} />
          )}
        </SectionCard>
      )}

      {tab === 5 && (
        <Box>
          <SectionCard variant="Default" title={t('tenants:fields.s3_prefix')}>
            <Typography
              sx={{
                fontFamily: typographyTokens.fontFamilyMono,
                fontSize: 13,
                color: semantic.text.primary,
                mb: 2,
              }}
            >
              {storagePrefix}
            </Typography>
            <Typography
              sx={{
                mb: 1,
                fontSize: 13,
                fontWeight: typographyTokens.fontWeightSemibold,
                color: semantic.text.secondary,
              }}
            >
              {t('tenants:fields.storage_paths')}
            </Typography>
            {!storagePathsLoaded ? (
              <LoadingSkeleton type="Detail" />
            ) : (
              <List dense disablePadding>
                {storageExamples.map((path) => (
                  <ListItem key={path} sx={{ px: 0 }}>
                    <ListItemText
                      primary={path}
                      primaryTypographyProps={{
                        fontFamily: typographyTokens.fontFamilyMono,
                        fontSize: 13,
                        color: semantic.text.secondary,
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </SectionCard>
          <TenantStorageSmokeTestCard tenantId={tenant.id} />
        </Box>
      )}

      {tab === 6 && (
        <SectionCard variant="Default" title={t('tenants:branding_title')}>
          <PlaceholderPanel message={t('tenants:branding_message')} />
        </SectionCard>
      )}

      {tab === 7 && (
        <TenantEntitlementsTab tenantId={tenant.id} />
      )}

      {tab === 8 && (
        <SectionCard variant="Default">
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('tenants:features.support_active')}
          </Alert>
          <Typography sx={{ mb: 2, fontSize: 13, color: semantic.text.secondary }}>
            {t('tenants:support_hint')}
          </Typography>
          <SupportTicketsTable tenantId={tenant.id} emptyMessage={t('tenants:no_support_tickets')} />
        </SectionCard>
      )}

      {tab === 9 && (
        <SectionCard variant="Default" noPadding>
          {!eventsLoaded ? (
            <LoadingSkeleton type="Table" embedded rows={3} columns={4} />
          ) : (
            <DataTable
              type="Default"
              embedded
              columns={eventColumns}
              rows={events}
              emptyMessage={t('tenants:no_events')}
            />
          )}
        </SectionCard>
      )}

      {tab === 10 && (
        <SectionCard variant="Default">
          <TenantSetupWizardTab tenantId={tenant.id} />
        </SectionCard>
      )}

      {tab === 11 && (
        <SectionCard variant="Default">
          <TenantProvisioningTab tenantId={tenant.id} launchReadinessTabIndex={3} />
        </SectionCard>
      )}

      {tab === 12 && (
        <SectionCard variant="Default">
          <TenantSchemaTab tenantId={tenant.id} />
        </SectionCard>
      )}

      {tab === 13 && (
        <SectionCard variant="Default">
          <Typography sx={{ mb: 2, fontSize: 13, color: semantic.text.secondary }}>
            {t('pricing:tenant.subtitle')}
          </Typography>
          <TenantBillingTab tenantId={tenant.id} />
        </SectionCard>
      )}

      {tab === 14 && (
        <SectionCard variant="Default" title={t('tenants:audit_title')}>
          <PlaceholderPanel message={t('tenants:audit_message')} />
        </SectionCard>
      )}
    </Box>
  );
}
